// gatewayProvider.tsx

import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { AutomaticMetricsType } from '../constants';
import { logGateway, logGatewaySignal } from '../logger';
import { REALTIME_GATEWAY_URL, isCloudUrl } from '../config/realtime';

import type {
  AnalysisUpdate,
  ErrorMsg,
  IncomingToDashboard,
  PresenceUpdate,
  Ready,
  SessionEnded,
  SessionStarted,
  IdentifyDashboard,
  AnalyzedMetrics,
  SessionMeta,
  LiveRepFrame,
  RepMetric,
} from '../types';

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';

export interface StationPresence {
  rtServiceOnline: boolean;
  status: 'DOWN' | 'STARTING' | 'UP' | 'DEGRADED' | 'UNKNOWN' | 'CAMERA_ISSUE';
  tabletOnline?: boolean;
  currentMember?: string | null;
  activeMembers?: string[];
  stationNumber?: number;
  details?: Record<string, unknown>;
}

type CloudIdentity = {
  gymId?: string;
  userId?: string;
  authToken?: string;
};
const DEFAULT_CLOUD_USER_ID = 'trainer-web';

const createEmptySessionMeta = (): SessionMeta => ({
  exerciseId: '',
  memberId: '',
  weight: 0,
  reps: 0,
  metrics: {
    concentric: {
      min: 0,
      max: 0,
      type: AutomaticMetricsType.Tempo,
    },
    eccentric: {
      min: 0,
      max: 0,
      type: AutomaticMetricsType.Tempo,
    },
  },
  activeMembers: [],
  stationNumber: 0,
});

const mapRepMetricToLiveFrame = (
  meta: SessionMeta,
  repIndex: number,
  metric?: RepMetric,
): LiveRepFrame | null => {
  if (!metric) return null;
  const concentricMetric = meta.metrics?.concentric;
  const eccentricMetric = meta.metrics?.eccentric;
  const concentricType = concentricMetric?.type ?? AutomaticMetricsType.Tempo;
  const eccentricType = eccentricMetric?.type ?? AutomaticMetricsType.Tempo;

  const upDuration =
    concentricType === AutomaticMetricsType.Velocity
      ? metric.upVelocity
      : metric.upDuration;
  const downDuration =
    eccentricType === AutomaticMetricsType.Velocity
      ? metric.downVelocity
      : metric.downDuration;

  return {
    rackNumber: meta.stationNumber,
    rep: repIndex,
    up: true,
    down: true,
    upDuration,
    downDuration,
    memberId: meta.memberId,
    exerciseId: meta.exerciseId,
    preset: {
      performedSets: 1,
      reps: meta.reps,
      weight: meta.weight,
      minConcentric: concentricMetric?.min,
      maxConcentric: concentricMetric?.max,
      minEccentric: eccentricMetric?.min,
      maxEccentric: eccentricMetric?.max,
      concentricType,
      eccentricType,
    },
  };
};

const rebuildFramesFromAnalyzedMetrics = (
  meta: SessionMeta,
  analyzed?: AnalyzedMetrics,
): LiveRepFrame[] => {
  if (!analyzed) return [];
  const entries = Object.entries(analyzed)
    .map(([repIndexStr, repMetric]) => ({
      repIndex: Number(repIndexStr),
      repMetric,
    }))
    .filter(({ repIndex }) => Number.isFinite(repIndex))
    .sort((a, b) => a.repIndex - b.repIndex);

  const frames: LiveRepFrame[] = [];
  for (const { repIndex, repMetric } of entries) {
    const frame = mapRepMetricToLiveFrame(meta, repIndex, repMetric);
    if (frame) frames.push(frame);
  }
  return frames;
};

const upsertRepFrame = (
  frames: LiveRepFrame[],
  repIndex: number,
  frame: LiveRepFrame,
): LiveRepFrame[] => {
  const existingIdx = frames.findIndex((f) => f.rep === repIndex);
  if (existingIdx >= 0) {
    const clone = frames.slice();
    clone[existingIdx] = frame;
    return clone;
  }
  const clone = [...frames, frame];
  clone.sort((a, b) => (a.rep ?? 0) - (b.rep ?? 0));
  return clone;
};

export interface LiveSessionState {
  sessionId: string;
  stationId: string;
  meta: SessionMeta;
  repFrames: LiveRepFrame[];
  lastTs: number;
  completed?: boolean;
}

export interface GatewayContextValue {
  status: ConnectionStatus;
  ready?: Ready['payload'];
  presenceByStation: Record<string, StationPresence>;
  liveSessionsByStation: Record<string, LiveSessionState | undefined>;
  errors: ErrorMsg[];
  connect: (subscriptions?: string[] | ['*'], identity?: CloudIdentity) => void;
  disconnect: () => void;
  updateSubscriptions: (subscriptions: string[] | ['*']) => void;
}

export const GatewayContext = createContext<GatewayContextValue>({
  status: 'disconnected',
  presenceByStation: {},
  liveSessionsByStation: {},
  errors: [],
  connect: () => {},
  disconnect: () => {},
  updateSubscriptions: () => {},
});

export const GatewayProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<any>(null);
  const shouldReconnectRef = useRef<boolean>(false);
  const connectingRef = useRef<boolean>(false);
  const subsRef = useRef<string[] | ['*']>(['*']);
  const urlRef = useRef<string | null>(null);
  const identifiedRef = useRef<boolean>(false);
  const identityRef = useRef<CloudIdentity>({});
  const repFramesRef = useRef<
    Record<string, { sessionId: string; frames: LiveRepFrame[] }>
  >({});

  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [ready, setReady] = useState<Ready['payload'] | undefined>(undefined);
  const [presenceByStation, setPresenceByStation] = useState<
    Record<string, StationPresence>
  >({});
  const [liveSessionsByStation, setLiveSessionsByStation] = useState<
    Record<string, LiveSessionState | undefined>
  >({});
  const [errors, setErrors] = useState<ErrorMsg[]>([]);

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const closeWs = useCallback(() => {
    clearReconnectTimer();
    try {
      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING)
      ) {
        // normal closure with reason for clearer gateway logs
        wsRef.current.close(1000, 'client_close');
      }
    } catch {}
    wsRef.current = null;
  }, []);

  const send = (msg: any) => {
    const wc = wsRef.current;
    const canSend = !!wc && wc.readyState === WebSocket.OPEN;
    if (!canSend) {
      logGateway('ws not open, cannot send', { state: wc?.readyState, msg });
      return;
    }
    try {
      wc!.send(JSON.stringify(msg));
      const destination = isCloudUrl(urlRef.current) ? 'cloud' : 'gateway';
      logGatewaySignal('SENT', msg as any, {
        from: 'dashboard',
        to: destination,
      });
    } catch (e) {
      logGateway('ws.send error', { error: (e as Error).message });
    }
  };

  const identify = useCallback((subs: string[] | ['*']) => {
    const stationIds = Array.isArray(subs) ? subs : ['*'];
    const url = urlRef.current;
    const cloudMode = isCloudUrl(url);

    if (cloudMode) {
      const ts = Date.now();
      const identity = identityRef.current || {};
      const gymId = identity.gymId;
      if (!gymId) {
        logGateway('cloud.identify skipped: missing gymId', {
          identity,
          url,
        });
        return;
      }
      const userId = identity.userId || DEFAULT_CLOUD_USER_ID;
      const authToken = identity.authToken;

      const identifyMsg = {
        type: 'cloud.identify' as const,
        ts,
        payload: {
          mode: 'trainerClient' as const,
          gymId,
          userId,
          authToken,
        },
      };

      logGatewaySignal('SENT', identifyMsg as any, {
        from: 'dashboard',
        to: 'cloud',
      });
      send(identifyMsg);

      const stationsPayload = stationIds.includes('*') ? undefined : stationIds;

      const subscribeMsg = {
        type: 'cloud.subscribe' as const,
        ts: ts + 1,
        payload: {
          gymId,
          stations: stationsPayload,
        },
      };

      logGatewaySignal('SENT', subscribeMsg as any, {
        from: 'dashboard',
        to: 'cloud',
      });
      send(subscribeMsg);
      return;
    }

    const msg: IdentifyDashboard = {
      type: 'identify',
      ts: Date.now(),
      payload: {
        clientType: 'dashboard',
        device: { platform: 'web', appVersion: 'mv-dashboard-gw-1' },
        subscriptions: { stationIds },
      },
    } as any;
    logGatewaySignal('SENT', msg as any, { from: 'dashboard', to: 'gateway' });
    send(msg);
  }, []);

  const connect = useCallback(
    (subscriptions: string[] | ['*'] = ['*'], identity?: CloudIdentity) => {
      const targetUrl = REALTIME_GATEWAY_URL;
      logGateway('connect() requested', {
        url: targetUrl,
        subscriptions,
        wsState: wsRef.current?.readyState,
        connecting: connectingRef.current,
      });
      // If we already have an open or connecting socket to the same URL, skip
      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING) &&
        urlRef.current === targetUrl
      ) {
        logGateway('connect() skipped: already OPEN/CONNECTING to same url', {
          url: targetUrl,
        });
        return;
      }
      // If a connect is already in progress, skip
      if (connectingRef.current) {
        logGateway('connect() skipped: connection already in progress');
        return;
      }

      // clear any pending reconnects and mark as reconnectable
      clearReconnectTimer();
      shouldReconnectRef.current = true;

      urlRef.current = targetUrl;
      subsRef.current = subscriptions;
      identityRef.current = identity ?? {};
      identifiedRef.current = false;
      setStatus('connecting');
      closeWs();
      connectingRef.current = true;
      const ws = new WebSocket(targetUrl);
      logGateway('ws.create', { url: targetUrl });
      wsRef.current = ws;
      ws.onopen = () => {
        connectingRef.current = false;
        setStatus('connected');
        logGateway('ws.open', { url: targetUrl });
        if (isCloudUrl(urlRef.current)) {
          identify(subsRef.current);
          identifiedRef.current = true;
        }
        // legacy: wait for ready before identify
      };
      ws.onmessage = (ev) => {
        try {
          const raw = JSON.parse(String(ev.data));
          let env: IncomingToDashboard | null = null;

          if (
            raw &&
            typeof raw === 'object' &&
            typeof raw.type === 'string' &&
            typeof raw.ts === 'number'
          ) {
            env = raw as IncomingToDashboard;
          } else if (
            raw &&
            typeof raw === 'object' &&
            raw.envelope &&
            typeof raw.envelope.type === 'string'
          ) {
            env = raw.envelope as IncomingToDashboard;
          } else {
            logGateway('ws.message unknown shape', { raw });
            return;
          }

          const currentUrl = urlRef.current;
          const cloudMode = isCloudUrl(currentUrl);
          const source = cloudMode ? 'cloud' : 'gateway';

          logGatewaySignal('RECV', env as any, {
            from: source,
            to: 'dashboard',
          });
          switch ((env as any).type) {
            case 'ready': {
              const rdy = (env as unknown as Ready).payload;
              setReady(rdy);
              if (!cloudMode && !identifiedRef.current) {
                identify(subsRef.current);
                identifiedRef.current = true;
              }
              break;
            }
            case 'presence_update': {
              const p = env as unknown as PresenceUpdate;
              const sid = (p as any).stationId as string;
              const pay = (p as any).payload || {};
              setPresenceByStation((prev) => ({
                ...prev,
                [sid]: {
                  rtServiceOnline: pay.rtServiceOnline ?? false,
                  status: pay.status ?? 'DOWN',
                  tabletOnline: pay.tabletOnline,
                  currentMember: pay.currentMember,
                  activeMembers: pay.activeMembers,
                  stationNumber: pay.stationNumber,
                  details: pay.details,
                },
              }));
              break;
            }
            case 'analysis_update': {
              const a = env as unknown as AnalysisUpdate;
              const payload = ((a as any).payload || {}) as
                | AnalysisUpdate['payload']
                | undefined;
              const meta = payload?.meta;
              if (!meta) {
                logGateway('analysis_update missing meta', {
                  stationId: (a as any).stationId,
                  sessionId: (a as any).sessionId,
                });
                break;
              }
              const stationId = (a as any).stationId as string;
              const sessionId = (a as any).sessionId as string;

              let cacheEntry = repFramesRef.current[stationId];
              if (!cacheEntry || cacheEntry.sessionId !== sessionId) {
                cacheEntry = { sessionId, frames: [] };
              }

              let nextFrames = cacheEntry.frames;
              const incomingFrame = payload?.frame;
              if (
                incomingFrame &&
                Number.isFinite(incomingFrame.repIndex) &&
                incomingFrame.metrics
              ) {
                const mappedFrame = mapRepMetricToLiveFrame(
                  meta,
                  incomingFrame.repIndex,
                  incomingFrame.metrics,
                );
                if (mappedFrame) {
                  nextFrames = upsertRepFrame(
                    nextFrames,
                    incomingFrame.repIndex,
                    mappedFrame,
                  );
                }
              } else if (payload?.analyzedMetrics) {
                nextFrames = rebuildFramesFromAnalyzedMetrics(
                  meta,
                  payload.analyzedMetrics,
                );
              }

              repFramesRef.current[stationId] = {
                sessionId,
                frames: nextFrames,
              };

              setLiveSessionsByStation((prev) => ({
                ...prev,
                [stationId]: {
                  stationId,
                  sessionId,
                  meta,
                  repFrames: repFramesRef.current[stationId].frames,
                  lastTs: (env as any).ts,
                  completed:
                    Boolean(payload?.frame?.completed) ||
                    Boolean(prev[stationId]?.completed),
                },
              }));
              break;
            }
            case 'session_started': {
              const s = env as unknown as SessionStarted;
              const stationId = (s as any).stationId as string;
              const sessionId = (s as any).sessionId as string;
              repFramesRef.current[stationId] = { sessionId, frames: [] };
              const pay = (s as any).payload || {};
              setLiveSessionsByStation((prev) => {
                const meta =
                  (pay as any).meta ||
                  prev[stationId]?.meta ||
                  createEmptySessionMeta();
                return {
                  ...prev,
                  [stationId]: {
                    stationId,
                    sessionId,
                    meta,
                    repFrames: [],
                    lastTs: (s as any).ts,
                    completed: false,
                  },
                };
              });
              break;
            }
            case 'session_ended': {
              const e = env as unknown as SessionEnded;
              setLiveSessionsByStation((prev) => {
                const next = { ...prev };
                const sid = (e as any).stationId as string;
                if (next[sid]?.sessionId === (e as any).sessionId) {
                  delete next[sid];
                }
                return next;
              });
              {
                const sid = (e as any).stationId as string;
                if (
                  repFramesRef.current[sid] &&
                  repFramesRef.current[sid].sessionId === (e as any).sessionId
                ) {
                  delete repFramesRef.current[sid];
                }
              }
              break;
            }
            case 'error': {
              const er = env as unknown as ErrorMsg;
              setErrors((prev) => [er as any, ...prev].slice(0, 50));
              break;
            }
            default:
              break;
          }
        } catch (e) {
          logGateway('ws.message parse error', {
            error: (e as Error).message,
            raw: ev as any,
          });
        }
      };
      ws.onclose = (evt) => {
        connectingRef.current = false;
        identifiedRef.current = false;
        setStatus('disconnected');
        logGateway('ws.close', { code: evt.code, reason: evt.reason });
        // schedule reconnect if allowed
        const urlNow = urlRef.current;
        const subsNow = subsRef.current;
        const identityNow = identityRef.current;
        if (
          shouldReconnectRef.current &&
          urlNow &&
          !reconnectTimerRef.current
        ) {
          reconnectTimerRef.current = setTimeout(
            () => {
              reconnectTimerRef.current = null;
              if (shouldReconnectRef.current && urlRef.current) {
                logGateway('ws.reconnect.retry', { url: urlNow });
                connect(subsNow, identityNow);
              }
            },
            1500 + Math.random() * 1000,
          );
        }
      };
      ws.onerror = (evt) => {
        connectingRef.current = false;
        logGateway('ws.error', { event: evt });
      };
    },
    [closeWs, identify],
  );

  const disconnect = useCallback(() => {
    // prevent further reconnects
    shouldReconnectRef.current = false;
    urlRef.current = null;
    identityRef.current = {};
    repFramesRef.current = {};
    clearReconnectTimer();
    logGateway('disconnect()');
    closeWs();
    setStatus('disconnected');
    setReady(undefined);
    setPresenceByStation({});
    setLiveSessionsByStation({});
  }, [closeWs]);

  const updateSubscriptions = useCallback(
    (subscriptions: string[] | ['*']) => {
      subsRef.current = subscriptions;
      identify(subscriptions);
    },
    [identify],
  );

  const value = useMemo<GatewayContextValue>(
    () => ({
      status,
      ready,
      presenceByStation,
      liveSessionsByStation,
      errors,
      connect,
      disconnect,
      updateSubscriptions,
    }),
    [
      status,
      ready,
      presenceByStation,
      liveSessionsByStation,
      errors,
      connect,
      disconnect,
      updateSubscriptions,
    ],
  );

  return (
    <GatewayContext.Provider value={value}>{children}</GatewayContext.Provider>
  );
};

export const useGateway = () => useContext(GatewayContext);
